/**
 * Features the dashboards describe but the product cannot yet answer (BON-534).
 *
 * Most of the hiding happens on the wire: the Overview endpoints simply do not
 * send staffing issues, region health, resupply, corp pipeline or sales
 * forecasting, so nothing can render them by accident. This module covers the
 * remainder — chrome that is purely a frontend decision, like an optional table
 * column or a sort option.
 *
 * Deleting a name from `UNBUILT_FEATURES` is the whole of "shipping" one here:
 * every guard is typed on the union below, so removing a name turns each guard
 * into a compile error you have to go and delete. That is the point — reversal
 * is self-locating rather than something to remember.
 *
 * One constant module rather than an environment variable, because the page has
 * to hide the same things on a phone as on a desktop and in every environment;
 * a value that can differ per environment is the opposite of that. And rather
 * than a prop on each card, because that puts the decision at every call site,
 * which is how something ends up hidden on desktop and merely dimmed on mobile.
 *
 * Note this HIDES. `MockedOverlay` dims a card and marks it mocked, which is a
 * different promise — "this exists but the data is fake" rather than "this does
 * not exist". Overview uses none of it.
 */
export const UNBUILT_FEATURES = [
  'staffing-issues',
  'region-health',
  'resupply',
  'corp-pipeline',
  'sales-forecasting',
] as const;

export type UnbuiltFeature = (typeof UNBUILT_FEATURES)[number];

/**
 * Whether a feature is still unbuilt. Takes the feature so the call site reads
 * as a statement about the product rather than a bare boolean.
 */
export function isUnbuilt(feature: UnbuiltFeature): boolean {
  return (UNBUILT_FEATURES as readonly string[]).includes(feature);
}

/** Keep only the entries whose feature exists. Entries with no `feature` are
 *  always kept, so the common case needs no annotation. */
export function withoutUnbuilt<T extends { feature?: UnbuiltFeature }>(entries: readonly T[]): T[] {
  return entries.filter((entry) => !entry.feature || !isUnbuilt(entry.feature));
}
